import style from './ToolTipWithImage.module.scss';

import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';

const DIRECTION = {
  TOP: 'top',
  BOTTOM: 'bottom',
  LEFT: 'left',
  RIGHT: 'right',
  AUTO: 'auto'
};

const ToolTipWithImage = ({
  open = false,
  children,
  renderComponent,
  footerComponent,
  customContainerClassName = '',
  customClassName = '',
  customArrowClassName = '',
  isArrowHidden = false,
  uniqueKey,
  hasNoHoveringDelay,
  direction = DIRECTION.BOTTOM
}) => {
  const className = 'c-ToolTipWithImage';
  const hoveringRef = useRef(false);
  const hoveringTimerRef = useRef(null);

  const contentClassName = [
    style[`${className}__content`],
    style[`${className}__content--${direction}`]
  ].join(' ');

  const arrowClassName = [
    style[`${className}__arrow`],
    style[`${className}__arrow--${direction}`],
    customArrowClassName
  ].join(' ');
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const { styles, attributes } = usePopper(
    referenceElement,
    popperElement,
    {
      placement: direction
    }
  );
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseEnter = (isNoTimer) => () => {
    if (isNoTimer || hasNoHoveringDelay) {
      setIsHovering(true);
      return;
    }

    if (hoveringRef.current === false) {
      clearTimeout(hoveringTimerRef.current);
    }

    hoveringRef.current = true;
    hoveringTimerRef.current = setTimeout(() => {
      if (hoveringRef.current) {
        setIsHovering(true);
      }
    }, 300);
  };

  const handleMouseLeave = (isNoTimer) => () => {
    if (isNoTimer) {
      setIsHovering(false);
      return;
    }

    clearTimeout(hoveringTimerRef.current);
    hoveringRef.current = false;
    setIsHovering(false);
  };

  useEffect(() => {
    if (referenceElement) {
      referenceElement.addEventListener('mouseenter', handleMouseEnter());
      referenceElement.addEventListener('mouseleave', handleMouseLeave());
    }
    if (popperElement) {
      popperElement.addEventListener('mouseenter', handleMouseEnter(true));
      popperElement.addEventListener('mouseleave', handleMouseLeave(true));
    }

    return () => {
      if (referenceElement) {
        referenceElement.removeEventListener(
          'mouseenter',
          handleMouseEnter()
        );
        referenceElement.removeEventListener(
          'mouseleave',
          handleMouseLeave()
        );
      }
      if (popperElement) {
        popperElement.removeEventListener(
          'mouseenter',
          handleMouseEnter(true)
        );
        popperElement.removeEventListener(
          'mouseleave',
          handleMouseLeave(true)
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceElement, popperElement]);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={`${style[className]} isolate absolute cursor-pointer ${customContainerClassName}`}
      ref={setReferenceElement}
      key={uniqueKey}
      onClick={(e) => e.stopPropagation()}>
      {children}
      {(open || isHovering) &&
        createPortal(
          <div
            className={`${contentClassName} z-over-intercom rounded-8 bg-npl-base-white ${customClassName} rounded-12 shadow-npl-styles-shadow-02`}
            style={styles.popper}
            ref={setPopperElement}
            {...attributes.popper}>
            {!isArrowHidden && (
              <div className={arrowClassName} data-popper-arrow></div>
            )}
            {renderComponent && renderComponent}
            {footerComponent && footerComponent()}
          </div>,
          document.body
        )}
    </div>
  );
};

ToolTipWithImage.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  direction: PropTypes.oneOf(Object.values(DIRECTION)),
  isArrowHidden: PropTypes.bool
};

export default ToolTipWithImage;
